import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { SidebarInset, SidebarProvider, SidebarTrigger, useSidebar } from '@/components/ui/sidebar';
import { AppSidebar } from './AppSidebar';
import { UserMenu } from './UserMenu';
import { EXPAND_AT } from './navItems';

const defaultOpen = () => typeof window !== 'undefined' && window.innerWidth >= EXPAND_AT;

/**
 * The app frame: the sidebar, and an inset holding a header strip and the page.
 *
 * Open state (D2): below 768px the sidebar is a drawer; from 768px to 1229px it starts collapsed to
 * icons; from 1230px it starts expanded. The trigger toggles it at any width, and crossing 1230px
 * resets it to that width's default. The sidebar_state cookie shadcn writes is never read.
 *
 * The header strip (Shell layout) is 56px, sticky, and pulled up over the page with -mb-14. From
 * 768px it is transparent and lets clicks through to the page, except for the user menu; below
 * 768px it is opaque and also holds the drawer's trigger.
 */
export function AppShell({ children }: { children: ReactNode }) {
  const [open, setOpen] = useState(defaultOpen);
  const wide = useRef(defaultOpen());

  useEffect(() => {
    const onResize = () => {
      const isWide = window.innerWidth >= EXPAND_AT;
      if (isWide !== wide.current) {
        wide.current = isWide;
        setOpen(isWide);
      }
    };
    window.addEventListener('resize', onResize);
    return () => window.removeEventListener('resize', onResize);
  }, []);

  return (
    <SidebarProvider
      open={open}
      onOpenChange={setOpen}
      className="h-svh overflow-hidden"
      // D28: the expanded sidebar is 14rem; the generated component's default stays 16rem.
      style={{ '--sidebar-width': '14rem' } as CSSProperties}
    >
      <AppSidebar />
      <SidebarInset className="h-svh min-w-0 overflow-hidden">
        <header className="pointer-events-auto sticky top-0 z-[1100] -mb-14 flex h-14 shrink-0 items-center justify-between bg-background px-3 md:pointer-events-none md:bg-transparent">
          <ShellTrigger />
          <div className="pointer-events-auto">
            <UserMenu />
          </div>
        </header>
        {children}
      </SidebarInset>
    </SidebarProvider>
  );
}

/** shadcn's sidebar trigger, at every width: it opens the drawer on phones and toggles the sidebar above. */
function ShellTrigger() {
  const { isMobile, openMobile, state } = useSidebar();
  return (
    <div className="pointer-events-auto flex items-center gap-2">
      <SidebarTrigger aria-label="Toggle navigation" aria-expanded={isMobile ? openMobile : state === 'expanded'} />
      <img src="/images/understory_logo_primary.png" alt="" className="size-7 md:hidden" />
    </div>
  );
}
